import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Trash2, Plus, Building2, Users } from 'lucide-react';
import { toast } from 'sonner';
import HealthUnitModal from '@/components/HealthUnitModal';

export default function ManageHealthUnits() {
  const [healthUnits, setHealthUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewUnitModal, setShowNewUnitModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHealthUnits();
  }, []);

  const fetchHealthUnits = async () => {
    try {
      const response = await axiosInstance.get('/health-units');
      setHealthUnits(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar unidades:', error);
      toast.error('Erro ao carregar unidades');
      setLoading(false);
    }
  };

  const handleDelete = async (unitId, unitName) => {
    // Check if any users are assigned to this unit
    try {
      const usersResponse = await axiosInstance.get(`/health-units/${unitId}/users-count`);
      const userCount = usersResponse.data.count;

      if (userCount > 0) {
        toast.error(`Esta unidade tem ${userCount} usuário(s) vinculado(s). Não é possível deletar.`);
        return;
      }
    } catch (error) {
      console.error('Erro ao verificar usuários:', error);
    }

    if (window.confirm(`Deseja realmente deletar a unidade "${unitName}"?\n\nEsta ação não pode ser desfeita.`)) {
      try {
        await axiosInstance.delete(`/health-units/${unitId}`);
        toast.success('Unidade deletada com sucesso!');
        fetchHealthUnits();
      } catch (error) {
        console.error('Erro ao deletar unidade:', error);
        if (error.response?.status === 400) {
          toast.error(error.response.data.detail || 'Não é possível deletar esta unidade');
        } else {
          toast.error('Erro ao deletar unidade');
        }
      }
    }
  };

  const handleUnitCreated = () => {
    setShowNewUnitModal(false);
    fetchHealthUnits();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)] p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Dashboard
          </Button>

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-foreground mb-2">
                Gerenciar Unidades de Saúde
              </h1>
              <p className="text-muted-foreground">
                Cadastre e gerencie as unidades de saúde do sistema
              </p>
            </div>
            <Button
              onClick={() => setShowNewUnitModal(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 h-12"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Unidade
            </Button>
          </div>
        </div>

        {/* Units List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Carregando unidades...</p>
          </div>
        ) : healthUnits.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">Nenhuma unidade cadastrada</p>
              <Button
                onClick={() => setShowNewUnitModal(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Primeira Unidade
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {healthUnits.map((unit) => (
              <Card key={unit.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-foreground mb-1">
                          {unit.nome}
                        </h3>
                        {unit.endereco && (
                          <p className="text-sm text-muted-foreground">
                            {unit.endereco}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => handleDelete(unit.id, unit.nome)}
                        variant="destructive"
                        className="rounded-full"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Deletar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Summary */}
        {healthUnits.length > 0 && (
          <div className="mt-8 text-center">
            <Badge variant="outline" className="px-4 py-2">
              Total: {healthUnits.length} unidade(s) cadastrada(s)
            </Badge>
          </div>
        )}
      </div>

      {/* New Unit Modal */}
      <HealthUnitModal
        open={showNewUnitModal}
        onClose={() => setShowNewUnitModal(false)}
        onSuccess={handleUnitCreated}
      />
    </div>
  );
}
